import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { Navbar } from "@/components/Navbar";
import { HeroSection } from "@/components/HeroSection";
import { ServicesSection } from "@/components/ServicesSection";
import { WhyChooseSection } from "@/components/WhyChooseSection";
import { JoinFleetSection } from "@/components/JoinFleetSection";
import { GetQuoteSection } from "@/components/GetQuoteSection";
import { TestimonialsSection } from "@/components/TestimonialsSection";
import { Footer } from "@/components/Footer";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";

// Mobile App Pages
import DriverRegister from "@/pages/DriverRegister";
import DriverLogin from "@/pages/DriverLogin";
import DriverDashboard from "@/pages/DriverDashboard";
import TripHistory from "@/pages/TripHistory";

// Admin Pages
import AdminLoginNew from "@/pages/AdminLoginNew";
import AdminRegister from "@/pages/AdminRegister";
import AdminDashboardNew from "@/pages/AdminDashboardNew";
import AdminUsers from "@/pages/AdminUsers";
import AdminForgotPassword from "@/pages/AdminForgotPassword";
import AdminJobs from "@/pages/AdminJobs";

// Jobs Pages
import JobsListing from "@/pages/JobsListing";
import JobDetails from "@/pages/JobDetails";

// Blog Pages
import BlogListing from "@/pages/BlogListing";
import BlogDetail from "@/pages/BlogDetail";
import AdminBlogs from "@/pages/AdminBlogs";
import { ALL_LANG_MAP } from "@/components/LanguageDropdown";
import { useParams } from "react-router-dom";

// Route resolver: if param is a lang code → show listing, else → show detail
const BlogOrLangRoute = () => {
  const { langOrSlug } = useParams();
  if (ALL_LANG_MAP[langOrSlug]) {
    return <BlogListing />;
  }
  return <BlogDetail />;
};

const HomePage = () => {
  const [activeSection, setActiveSection] = useState(0);

  const sections = [
    { id: 'home', name: 'Home' },
    { id: 'services', name: 'Services' },
    { id: 'why-us', name: 'Why Us' },
    { id: 'join-fleet', name: 'Join Fleet' },
    { id: 'get-quote', name: 'Get Quote' },
    { id: 'testimonials', name: 'Testimonials' },
    { id: 'contact', name: 'Contact' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight / 2;
      
      sections.forEach((section, index) => {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(index);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <Navbar />
      
      {/* Slide Navigation Dots */}
      <div className="slide-nav hidden lg:flex">
        {sections.map((section, index) => (
          <button
            key={section.id}
            onClick={() => scrollToSection(section.id)}
            className={`slide-nav-dot ${activeSection === index ? 'active' : ''}`}
            aria-label={`Go to ${section.name}`}
            title={section.name}
          />
        ))}
      </div>

      <div className="snap-section">
        <HeroSection />
      </div>
      <div className="snap-section">
        <ServicesSection />
      </div>
      <div className="snap-section">
        <WhyChooseSection />
      </div>
      <div className="snap-section">
        <JoinFleetSection />
      </div>
      <div className="snap-section">
        <GetQuoteSection />
      </div>
      <div className="snap-section">
        <TestimonialsSection />
      </div>
      <div className="snap-section">
        <Footer />
      </div>
    </>
  );
};

function App() {
  useEffect(() => {
    if ('serviceWorker' in navigator && window.location.pathname.startsWith('/mobileapp')) {
      navigator.serviceWorker.register('/sw.js');
    }
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Website */}
          <Route path="/" element={<HomePage />} />
          <Route path="/driver-jobs" element={<JobsListing />} />
          <Route path="/driver-jobs/:id" element={<JobDetails />} />
          <Route path="/blog" element={<BlogListing />} />
          <Route path="/blog/:langOrSlug/:slug" element={<BlogDetail />} />
          <Route path="/blog/:langOrSlug" element={<BlogOrLangRoute />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          
          {/* Mobile App */}
          <Route path="/mobileapp" element={<DriverLogin />} />
          <Route path="/mobileapp/register" element={<DriverRegister />} />
          <Route path="/mobileapp/login" element={<DriverLogin />} />
          <Route path="/mobileapp/dashboard" element={<DriverDashboard />} />
          <Route path="/mobileapp/history" element={<TripHistory />} />
          
          {/* Admin Panel */}
          <Route path="/admin" element={<AdminLoginNew />} />
          <Route path="/admin/register" element={<AdminRegister />} />
          <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
          <Route path="/admin/dashboard" element={<AdminDashboardNew />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/jobs" element={<AdminJobs />} />
          <Route path="/admin/blogs" element={<AdminBlogs />} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
